import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import Box from '@mui/material/Box';
import { SelectLanguage, TextField } from 'components/common';
import { Button, Divider, IconButton, Typography } from '@mui/material';
import { AddOutlined, DeleteOutline, TranslateOutlined } from '@mui/icons-material';
import { FormControlContainer } from './CreativeTextsComponents';
import scrollIntoView from 'utils/scrollIntoView';
import { AnimatedContainer, LocalizationsVersionContainer } from './AddCreativeTextLocalizations.styles';

const AddCreativeTextLocalizations: FC = () => {
  const {
    control,
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'localizations',
  });
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);

  const handleAddLocalization = useCallback(() => {
    append({
      text: '',
      language: '',
    });
    scrollIntoView(`[id*="localizations-"]`);
  }, [append]);

  const handleRemoveLocalization = useCallback(
    (index: number) => {
      setRemovingIndex(index);
      setTimeout(() => {
        remove(index);
        setRemovingIndex(null);
      }, 500);
    },
    [remove],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
      }}
    >
      {fields.map((field, index) => {
        const isRemoving = removingIndex === index;

        return (
          <AnimatedContainer
            key={`localizations-${index}`}
            id={`localizations-${index}`}
            className={isRemoving ? 'removing' : ''}
          >
            <Divider
              orientation="horizontal"
              variant="middle"
              flexItem
              sx={{ my: 0, mx: 0 }}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <TranslateOutlined color={'primary'} />
                <Typography variant={'subtitle1'}>
                  Localized version #{index + 1}
                </Typography>
              </Box>
              <IconButton
                onClick={() => {
                  handleRemoveLocalization(index);
                }}
                disabled={isRemoving}
              >
                <DeleteOutline />
              </IconButton>
            </Box>
            <LocalizationsVersionContainer key={field.id}>
              <FormControlContainer title="Text">
                <TextField
                  name={`localizations.${index}.text`}
                  placeholder={'Value'}
                  multiline
                  inputProps={{
                    sx: {
                      '&.MuiInputBase-input': {
                        minHeight: '150px',
                        borderRadius: 0,
                      },
                    },
                  }}
                />
              </FormControlContainer>
              <FormControlContainer title="Language">
                <SelectLanguage name={`localizations.${index}.language`} />
              </FormControlContainer>
            </LocalizationsVersionContainer>
          </AnimatedContainer>
        );
      })}
      <Box>
        <Button
          onClick={handleAddLocalization}
          startIcon={<AddOutlined />}
          variant={'text'}
        >
          Add localized version
        </Button>
      </Box>
    </Box>
  );
};

export default AddCreativeTextLocalizations;
